import React, { useState } from 'react';
import { Layers, PackageMinus, ClipboardList, BarChart3, RefreshCw, Plus, X, Clock, CheckCircle2, Database } from 'lucide-react';

const parseQtyNumber = (qtyValue) => {
  if (qtyValue === undefined || qtyValue === null) return 0;
  const n = parseFloat(String(qtyValue));
  return isNaN(n) ? 0 : n;
};

const BottomPanel = ({ elementData, onClose, onRefresh, isRefreshing, onCreateMR }) => {
  const [activeTab, setActiveTab] = useState('info');

  if (!elementData) return null;

  const sap = elementData.sapData || {};
  const pendingMRs = sap.pending_mrs || [];
  const goodsIssued = sap.goods_issued || [];
  const localHistory = sap.local_history || [];

  const designQty = parseQtyNumber(elementData.quantity);
  const issuedQty = goodsIssued.reduce((sum, m) => sum + parseQtyNumber(m.qty), 0);
  const remainingQty = Math.max(designQty - issuedQty, 0);
  const progressPct = designQty > 0 ? Math.min(100, (issuedQty / designQty) * 100) : 0;

  return (
    <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-slate-200 shadow-2xl z-50 transition-all duration-300 flex flex-col max-h-[350px]" dir="ltr">

      {/* 1. Panel header */}
      <div className="bg-slate-900 text-white px-5 py-3 flex justify-between items-center border-b border-slate-800 shrink-0">
        <div className="flex items-center gap-3">
          <span className="bg-blue-600 text-white text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-wider">
            {elementData.phase || 'Construction'}
          </span>
          <h3 className="text-sm font-black tracking-wide m-0 truncate max-w-md">
            {elementData.name} <span className="text-slate-400 font-normal text-xs">(ID: {elementData.dbId})</span>
          </h3>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={onRefresh} disabled={isRefreshing} className="bg-slate-800 hover:bg-slate-700 text-slate-200 px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors border border-slate-700 disabled:opacity-50 disabled:cursor-not-allowed">
            <RefreshCw size={13} className={isRefreshing ? 'animate-spin' : ''} /> Sync SAP
          </button>

          <button onClick={() => onCreateMR(elementData)} className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors shadow-sm">
            <Plus size={14} /> Request Materials (MR)
          </button>

          <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded-lg transition-colors ml-2">
            <X size={18} />
          </button>
        </div>
      </div>

      {/* 2. Panel tabs */}
      <div className="flex border-b border-slate-200 bg-slate-50 px-4 gap-2 shrink-0 overflow-x-auto">
        <button onClick={() => setActiveTab('info')} className={`py-2.5 px-4 text-xs font-bold flex items-center gap-2 border-b-2 transition-all shrink-0 ${activeTab === 'info' ? 'border-blue-600 text-blue-600 bg-white shadow-sm' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
          <Layers size={14} /> Element Info
        </button>

        <button onClick={() => setActiveTab('issued')} className={`py-2.5 px-4 text-xs font-bold flex items-center gap-2 border-b-2 transition-all shrink-0 ${activeTab === 'issued' ? 'border-blue-600 text-blue-600 bg-white shadow-sm' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
          <PackageMinus size={14} /> Goods Issued ({goodsIssued.length})
        </button>

        <button onClick={() => setActiveTab('mrs')} className={`py-2.5 px-4 text-xs font-bold flex items-center gap-2 border-b-2 transition-all shrink-0 ${activeTab === 'mrs' ? 'border-blue-600 text-blue-600 bg-white shadow-sm' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
          <ClipboardList size={14} /> Pending MRs ({pendingMRs.length})
        </button>

        <button onClick={() => setActiveTab('quantities')} className={`py-2.5 px-4 text-xs font-bold flex items-center gap-2 border-b-2 transition-all shrink-0 ${activeTab === 'quantities' ? 'border-blue-600 text-blue-600 bg-white shadow-sm' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
          <BarChart3 size={14} /> Quantities & Progress
        </button>

        <button onClick={() => setActiveTab('history')} className={`py-2.5 px-4 text-xs font-bold flex items-center gap-2 border-b-2 transition-all shrink-0 ${activeTab === 'history' ? 'border-blue-600 text-blue-600 bg-white shadow-sm' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
          <Database size={14} /> Order History ({localHistory.length})
        </button>
      </div>

      {/* 3. Tab content */}
      <div className="flex-1 overflow-y-auto p-4 bg-white text-xs">

        {activeTab === 'info' && (
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
              <span className="text-slate-400 block mb-1 font-semibold uppercase text-[10px]">Category</span>
              <span className="font-bold text-slate-800 text-sm truncate block">{elementData.category}</span>
            </div>

            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
              <span className="text-slate-400 block mb-1 font-semibold uppercase text-[10px]">Family / Type</span>
              <span className="font-bold text-slate-800 text-sm truncate block" title={elementData.family}>{elementData.family}</span>
            </div>

            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
              <span className="text-slate-400 block mb-1 font-semibold uppercase text-[10px]">Location / Level</span>
              <span className="font-bold text-slate-800 text-sm truncate block">{elementData.level}</span>
            </div>

            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
              <span className="text-slate-400 block mb-1 font-semibold uppercase text-[10px]">Structural Material</span>
              <span className="font-bold text-blue-600 text-sm truncate block" title={elementData.material}>{elementData.material}</span>
            </div>

            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
              <span className="text-slate-400 block mb-1 font-semibold uppercase text-[10px]">Volume</span>
              <span className="font-bold text-slate-800 text-sm truncate block">{elementData.volume}</span>
            </div>

            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
              <span className="text-slate-400 block mb-1 font-semibold uppercase text-[10px]">Surface Area</span>
              <span className="font-bold text-slate-800 text-sm truncate block">{elementData.area}</span>
            </div>

            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
              <span className="text-slate-400 block mb-1 font-semibold uppercase text-[10px]">Length</span>
              <span className="font-bold text-slate-800 text-sm truncate block">{elementData.length}</span>
            </div>

            {elementData.rebarWeightKg > 0 && (
              <div className="bg-amber-50 p-3 rounded-xl border border-amber-100">
                <span className="text-amber-500 block mb-1 font-semibold uppercase text-[10px]">Reinforcement (Rebar)</span>
                <span className="font-bold text-amber-700 text-sm truncate block">{elementData.rebarWeightKg} KG</span>
              </div>
            )}

            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
              <span className="text-slate-400 block mb-1 font-semibold uppercase text-[10px]">Asset Status (SAP)</span>
              <span className="font-bold text-emerald-600 text-sm truncate block">{sap.status || 'UNDER CONSTRUCTION'}</span>
            </div>
          </div>
        )}

        {activeTab === 'issued' && (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-100 text-slate-600 text-[11px] uppercase">
                  <th className="p-3 border-b border-slate-200">GI Doc #</th>
                  <th className="p-3 border-b border-slate-200">Material Code</th>
                  <th className="p-3 border-b border-slate-200">Issued Qty</th>
                  <th className="p-3 border-b border-slate-200">Date</th>
                  <th className="p-3 border-b border-slate-200">Status</th>
                </tr>
              </thead>
              <tbody>
                {goodsIssued.length === 0 ? (
                  <tr><td colSpan="5" className="text-center p-6 text-slate-400 font-medium italic">No materials issued from warehouse yet.</td></tr>
                ) : (
                  goodsIssued.map((m, idx) => (
                    <tr key={idx} className="hover:bg-slate-50 transition-colors">
                      <td className="p-3 border-b border-slate-100 font-bold text-blue-600">{m.doc_num}</td>
                      <td className="p-3 border-b border-slate-100 font-bold text-slate-800">{m.material}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-700 font-medium">{m.qty}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-500">{m.date}</td>
                      <td className="p-3 border-b border-slate-100">
                        <span className="bg-emerald-100 text-emerald-800 px-2.5 py-1 rounded-md text-[11px] font-bold flex items-center w-max gap-1">
                          <CheckCircle2 size={14}/> {m.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'mrs' && (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-100 text-slate-600 text-[11px] uppercase">
                  <th className="p-3 border-b border-slate-200">MR Doc #</th>
                  <th className="p-3 border-b border-slate-200">Material Code</th>
                  <th className="p-3 border-b border-slate-200">Requested Qty</th>
                  <th className="p-3 border-b border-slate-200">Status</th>
                </tr>
              </thead>
              <tbody>
                {pendingMRs.length === 0 ? (
                  <tr><td colSpan="4" className="text-center p-6 text-slate-400 font-medium italic">No pending material requisitions.</td></tr>
                ) : (
                  pendingMRs.map((m, idx) => (
                    <tr key={idx} className="hover:bg-slate-50 transition-colors">
                      <td className="p-3 border-b border-slate-100 font-bold text-orange-600">{m.mr_num}</td>
                      <td className="p-3 border-b border-slate-100 font-bold text-slate-800">{m.material}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-700 font-medium">{m.qty}</td>
                      <td className="p-3 border-b border-slate-100">
                        <span className="bg-amber-100 text-amber-800 px-2.5 py-1 rounded-md text-[11px] font-bold flex items-center w-max gap-1">
                          <Clock size={14}/> {m.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'quantities' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center bg-slate-50 p-4 rounded-xl border border-slate-100">
              <div>
                <span className="text-slate-400 text-[11px] uppercase font-semibold block mb-1">Design Quantity (Revit)</span>
                <span className="font-black text-slate-800 text-lg">{designQty.toFixed(2)} {elementData.uom}</span>
              </div>
              <div>
                <span className="text-slate-400 text-[11px] uppercase font-semibold block mb-1">Issued from SAP</span>
                <span className="font-black text-emerald-600 text-lg">{issuedQty.toFixed(2)} {elementData.uom}</span>
              </div>
              <div>
                <span className="text-slate-400 text-[11px] uppercase font-semibold block mb-1">Remaining Quantity</span>
                <span className="font-black text-amber-600 text-lg">{remainingQty.toFixed(2)} {elementData.uom}</span>
              </div>
              <div className="w-1/3">
                <div className="flex justify-between text-xs font-bold mb-2">
                  <span className="text-slate-600">Execution Progress</span>
                  <span className="text-emerald-600">{progressPct.toFixed(0)}%</span>
                </div>
                <div className="w-full bg-slate-200 h-3 rounded-full overflow-hidden shadow-inner">
                  <div className="bg-emerald-500 h-full rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }}></div>
                </div>
              </div>
            </div>
            {elementData.rebarWeightKg > 0 && (
              <div className="bg-amber-50 p-4 rounded-xl border border-amber-100 flex justify-between items-center">
                <span className="text-amber-700 text-[11px] uppercase font-semibold">Reinforcement Linked to this Element</span>
                <span className="font-black text-amber-700 text-lg">{elementData.rebarWeightKg} KG</span>
              </div>
            )}
          </div>
        )}

        {activeTab === 'history' && (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-100 text-slate-600 text-[11px] uppercase">
                  <th className="p-3 border-b border-slate-200">MR Number</th>
                  <th className="p-3 border-b border-slate-200">Material Code</th>
                  <th className="p-3 border-b border-slate-200">Quantity</th>
                  <th className="p-3 border-b border-slate-200">Requested By</th>
                  <th className="p-3 border-b border-slate-200">Date</th>
                  <th className="p-3 border-b border-slate-200">Status</th>
                </tr>
              </thead>
              <tbody>
                {localHistory.length === 0 ? (
                  <tr><td colSpan="6" className="text-center p-6 text-slate-400 font-medium italic">No requests recorded for this specific element in the local database yet.</td></tr>
                ) : (
                  localHistory.map((h, idx) => (
                    <tr key={idx} className={`transition-colors ${h.is_simulated ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-slate-50'}`}>
                      <td className="p-3 border-b border-slate-100 font-bold text-blue-600">{h.sap_mr_number}</td>
                      <td className="p-3 border-b border-slate-100 font-bold text-slate-800">{h.material_code}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-700 font-medium">{h.quantity} {h.unit}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-700 font-medium">{h.created_by || '-'}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-500">{h.created_at ? new Date(h.created_at).toLocaleString() : '-'}</td>
                      <td className="p-3 border-b border-slate-100">
                        {h.is_simulated ? (
                          <span className="inline-block bg-amber-100 text-amber-700 text-[10px] font-black px-2 py-0.5 rounded-full border border-amber-300">SIMULATED</span>
                        ) : (
                          <span className="inline-block bg-emerald-100 text-emerald-700 text-[10px] font-black px-2 py-0.5 rounded-full border border-emerald-300">REAL</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </div>
  );
};

export default BottomPanel;
